import { useState, useEffect } from 'react';
import { ref, get, update, remove } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuthState } from '../hooks/useAuth';
import { DateTime } from 'luxon';
import toast from 'react-hot-toast';
import ConfirmDialog from './ConfirmDialog';
import './EmailDetails.css';

function EmailDetails({ email, folder, onBack, onCompose, onEmailRead, user: userProp }) {
  const [userFromAuth] = useAuthState();
  const user = userProp || userFromAuth;
  const [conversation, setConversation] = useState([email]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (user) {
      loadConversation();
    }
  }, [email, user]);

  useEffect(() => {
    if (user && folder === 'inbox' && (!email.read || email.read === false)) {
      markEmailAsRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email.id, folder, user]);

  const markEmailAsRead = async () => {
    if (!user || folder !== 'inbox' || (email.read === true)) {
      return;
    }

    try {
      const emailRef = ref(database, `emails/${user.uid}/inbox/${email.id}`);
      await update(emailRef, { read: true });
      
      // Notify parent component to update the email list
      if (onEmailRead) {
        onEmailRead(email.id);
      }
    } catch (error) {
      console.error('Error marking email as read:', error);
    }
  };

  const loadConversation = async () => {
    if (!user) return;
    try {
      const conv = await getEmailConversation(email, user);
      setConversation(conv);
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEmailConversation = async (email, user) => {
    const stripPrefixes = (s = '') => {
      let t = String(s).trim();
      const rx = /^(re|fwd?|fw|aw|sv|res?|enc):\s*/i;
      while (rx.test(t)) t = t.replace(rx, '').trim();
      return t;
    };

    const norm = (s = '') => {
      return stripPrefixes(s).replace(/\s+/g, ' ').trim().toLowerCase();
    };

    const toList = (map, folder) => {
      return Object.entries(map || {}).map(([id, m]) => ({ id, ...m, __folder: folder }));
    };

    const toMillis = (x) => {
      if (x == null) return 0;
      if (typeof x === 'number') return x;
      const t = Date.parse(x);
      return Number.isFinite(t) ? t : 0;
    };

    const [inboxSnap, sentSnap] = await Promise.all([
      get(ref(database, `emails/${user.uid}/inbox`)),
      get(ref(database, `emails/${user.uid}/sent`))
    ]);

    const inbox = inboxSnap.exists() ? toList(inboxSnap.val(), 'inbox') : [];
    const sent = sentSnap.exists() ? toList(sentSnap.val(), 'sent') : [];
    const all = [...inbox, ...sent];

    const baseNorm = norm(email?.subject || '');

    const related = all.filter(m => {
      if (!m) return false;
      if (m.id === email.id) return false;
      if (norm(m.subject || '') !== baseNorm) return false;

      if (m.__folder === 'inbox') {
        const a = String(m.from_email || '').toLowerCase();
        const b = String(email?.from_email || '').toLowerCase();
        if (a !== b) return false;
      }

      return true;
    });

    related.push({ ...email, __folder: email.__folder || 'current' });
    related.sort((a, b) => toMillis(b.timestamp ?? b.date) - toMillis(a.timestamp ?? a.date));

    return related;
  };

  const handleReply = () => {
    onCompose(
      email.from_email,
      email.subject.startsWith('Re: ') ? email.subject : `Re: ${email.subject}`,
      `\n\n<div class="gmail_quote gmail_quote_container"><div dir="ltr" class="gmail_attr">On ${email.timestamp}, ${email.from_email} wrote:<br></div><blockquote class="gmail_quote" style="margin:0px 0px 0px 0.8ex;border-left:1px solid rgb(204,204,204);padding-left:1ex">${email.body}</blockquote></div>`
    );
  };

  const handleForward = () => {
    onCompose(
      '',
      email.subject.startsWith('Fwd: ') ? email.subject : `Fwd: ${email.subject}`,
      `\n\n<div class="gmail_quote gmail_quote_container"><div dir="ltr" class="gmail_attr">On ${email.timestamp}, ${email.from_email} wrote:<br></div><blockquote class="gmail_quote" style="margin:0px 0px 0px 0.8ex;border-left:1px solid rgb(204,204,204);padding-left:1ex">${email.body}</blockquote></div>`
    );
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);

    if (!user) {
      toast.error('User not found');
      return;
    }

    try {
      const emailRef = ref(database, `emails/${user.uid}/${folder}/${email.id}`);
      await remove(emailRef);
      
      // Navigate back to list after deletion
      onBack();
      
      // Show success message
      toast.success('Email deleted successfully');
    } catch (error) {
      console.error('Error deleting email:', error);
      toast.error(`Failed to delete email: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="email-details">
        <div className="loading-state">Loading...</div>
      </div>
    );
  }

  return (
    <div className="email-details">
      <div className="modal-content">
        <div className="email-details-actions">
          <div className="left-actions">
            <button className="icon-button" onClick={onBack} title="Back">
              <i className="fas fa-arrow-left"></i>
            </button>
            <button className="icon-button" title="Delete" onClick={handleDeleteClick}>
              <i className="fas fa-trash"></i>
            </button>
          </div>
          <div className="right-actions">
            <button className="icon-button" onClick={handleReply} title="Reply">
              <i className="fas fa-reply"></i>
            </button>
            <button className="icon-button" onClick={handleForward} title="Forward">
              <i className="fas fa-share"></i>
            </button>
          </div>
        </div>

        <div className="email-details-content">
          {conversation.map((msg, index) => (
            <div key={msg.id || index} className="conversation-message">
              <div className="email-details-header">
                <h1 className="subject">{msg.subject || '(No Subject)'}</h1>
                <div className="from">
                  <strong>From:</strong> {msg.from_email}
                </div>
                <div className="to">
                  <strong>To:</strong> {msg.to_email}
                </div>
                <div className="date">
                  {DateTime.fromISO(msg.timestamp, { zone: 'utc' })
                    .setZone(Intl.DateTimeFormat().resolvedOptions().timeZone)
                    .toFormat("hh:mm a, dd MMMM, yyyy")}
                </div>
              </div>
              <div
                className="email-body"
                dangerouslySetInnerHTML={{ __html: msg.body?.replaceAll('href', 'target="_blank" href') || '' }}
              />
            </div>
          ))}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Email"
        message="Are you sure you want to delete this email? This action cannot be undone."
      />
    </div>
  );
}

export default EmailDetails;
